/**
 * JP Gems & Jewels — Debtor Report
 * -----------------------------------------------------------------------------
 * A daily ledger of who owes money. For each client / invoice the team records
 * the GOLD and DIAMOND amount and due date (diamond weight removed per founder),
 * so the founder can quickly see which party owes what and when it's due, and
 * follow up for payment.
 *
 * - Add a party (new or existing — a client can appear multiple times).
 * - Due dates have a "+days" quick box (type 10/20/40 → auto-date from the entry
 *   date). Re-opening Add for an EXISTING client repeats that client's last
 *   gold/diamond term automatically (date = today, due = today + same gap).
 * - Filter by client, sales person, due status; search by name/invoice; sort.
 * - Flag rows for follow-up. Download the filtered report as PDF or Excel.
 * Persists to the backend (shared across devices) via debtorStore.
 */
import React, { useMemo, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  ClipboardList, Plus, Edit3, Trash2, Search, FileSpreadsheet, FileText, AlertTriangle, CheckCircle2, Filter,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useDebtorStore, debtorTotal, type DebtorEntry } from '../../store/debtorStore'
import { useAuthStore } from '../../store/authStore'

const todayStr = () => new Date().toISOString().slice(0, 10)
const money = (n: number) => '₹' + Math.round(n || 0).toLocaleString('en-IN')
const inr = (n: number) => Math.round(n || 0).toLocaleString('en-IN')
/** Gold weight in grams, up to 3 decimals, with a "g" suffix. */
const wt = (n: number) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 }) + ' g'
const plusDays = (iso: string, days: number) => { const d = new Date(iso || todayStr()); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10) }
const diffDays = (from: string, to: string): string => {
  const a = Date.parse(from), b = Date.parse(to)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return ''
  return String(Math.round((b - a) / 86400000))
}
/** Days from today until a due date (negative = overdue). '' due date → null. */
const daysLeft = (due: string): number | null => {
  if (!due) return null
  const d = Math.round((Date.parse(due) - Date.parse(todayStr())) / 86400000)
  return Number.isFinite(d) ? d : null
}
/** The earliest (most urgent) due date across gold + diamond for an entry. */
const soonestDue = (e: DebtorEntry) => [e.goldDueDate, e.diaDueDate].filter(Boolean).sort()[0] || ''
/** Small "days from the due date" label: overdue (red) or days left. */
const dueDays = (due: string): { text: string; cls: string } | null => {
  const dl = daysLeft(due)   // + = days left, − = overdue
  if (dl === null) return null
  if (dl < 0) return { text: `${Math.abs(dl)}d overdue`, cls: 'text-red-600 font-semibold' }
  if (dl <= 7) return { text: `${dl}d left`, cls: 'text-amber-700' }
  return { text: `${dl}d left`, cls: 'text-steel-400' }
}
const CATEGORIES = ['B-B', 'B-C']
/** Is a YYYY-MM-DD date within [from, to]? Empty bounds are open; empty date fails when a bound is set. */
const inRange = (d: string, from: string, to: string) => {
  if (!from && !to) return true
  if (!d) return false
  if (from && d < from) return false
  if (to && d > to) return false
  return true
}

/** Column-header date-range filter: From/To calendar pickers + quick presets. */
const DateRangeFilter: React.FC<{ label: string; from: string; to: string; onChange: (from: string, to: string) => void }> = ({ label, from, to, onChange }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const active = !!(from || to)
  const openPanel = () => {
    if (btnRef.current) { const r = btnRef.current.getBoundingClientRect(); setPos({ top: r.bottom + 4, left: Math.min(Math.max(8, r.left), window.innerWidth - 270) }) }
    setOpen(true)
  }
  const preset = (f: string, t: string) => { onChange(f, t) }
  const t0 = todayStr()
  const Btn: React.FC<{ f: string; t: string; children: React.ReactNode }> = ({ f, t, children }) => (
    <button onClick={() => preset(f, t)} className="px-1.5 py-0.5 rounded bg-steel-100 hover:bg-primary-100 hover:text-primary-700">{children}</button>
  )
  return (
    <>
      <button ref={btnRef} type="button" onClick={e => { e.stopPropagation(); open ? setOpen(false) : openPanel() }}
        className={clsx('mt-1 w-full flex items-center justify-center gap-1 text-[10px] font-medium rounded border px-1 py-0.5',
          active ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-steel-200 bg-white text-steel-500 hover:bg-steel-50')}>
        <Filter size={10} /> {active ? 'Filtered' : 'Filter'}
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="fixed z-[61] bg-white border border-steel-200 rounded-lg shadow-lg p-3 text-xs w-64" style={{ top: pos.top, left: pos.left }}>
            <div className="flex items-center justify-between mb-2"><span className="font-semibold text-steel-700">{label} · date range</span>{active && <button onClick={() => preset('', '')} className="text-primary-600 hover:underline">Clear</button>}</div>
            <div className="flex flex-wrap gap-1 mb-2">
              <Btn f={t0} t={t0}>Today</Btn>
              <Btn f={plusDays(t0, -7)} t={t0}>Last 7d</Btn>
              <Btn f={plusDays(t0, -30)} t={t0}>Last 30d</Btn>
              <Btn f={t0} t={plusDays(t0, 7)}>Next 7d</Btn>
              <Btn f={t0} t={plusDays(t0, 30)}>Next 30d</Btn>
              <Btn f={t0.slice(0, 8) + '01'} t={t0}>This month</Btn>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="text-steel-500">From</span><input type="date" value={from} onChange={e => onChange(e.target.value, to)} className="form-input w-full mt-0.5 text-xs" /></label>
              <label className="block"><span className="text-steel-500">To</span><input type="date" value={to} onChange={e => onChange(from, e.target.value)} className="form-input w-full mt-0.5 text-xs" /></label>
            </div>
          </div>
        </>, document.body)}
    </>
  )
}

/** A coloured due-date cell: red if overdue, amber if within 7 days, else plain. */
const DueCell: React.FC<{ due: string }> = ({ due }) => {
  if (!due) return <span className="text-steel-300">—</span>
  const dl = daysLeft(due)
  const cls = dl === null ? 'text-steel-600'
    : dl < 0 ? 'bg-red-100 text-red-700 font-semibold'
    : dl <= 7 ? 'bg-amber-100 text-amber-800 font-medium'
    : 'text-steel-600'
  const lab = dueDays(due)
  return (
    <span className="inline-flex flex-col items-center leading-tight">
      <span className={clsx('px-1.5 py-0.5 rounded whitespace-nowrap', cls)}>{due}</span>
      {lab && <span className={clsx('text-[10px]', lab.cls)}>{lab.text}</span>}
    </span>
  )
}

interface EntryForm {
  date: string; clientName: string; category: string; invoiceNo: string
  goldWeight: string; goldAmount: string; goldDueDate: string; goldDays: string
  diaAmount: string; diaDueDate: string; diaDays: string
  salesPerson: string; remarks: string
}
const emptyForm = (): EntryForm => ({
  date: todayStr(), clientName: '', category: '', invoiceNo: '',
  goldWeight: '', goldAmount: '', goldDueDate: '', goldDays: '',
  diaAmount: '', diaDueDate: '', diaDays: '',
  salesPerson: '', remarks: '',
})

const DebtorReport: React.FC = () => {
  const { entries, addEntry, updateEntry, setPaid, removeEntry } = useDebtorStore()
  const { user } = useAuthStore()

  const [search, setSearch] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [salesFilter, setSalesFilter] = useState('')
  const [dueFilter, setDueFilter] = useState<'all' | 'overdue' | 'due7' | 'upcoming'>('all')
  const [sortBy, setSortBy] = useState<'due' | 'name' | 'invoice' | 'date'>('due')
  // Column date-range filters: entry Date, Gold Due date, Diamond Due date.
  const [dateRange, setDateRange] = useState({ from: '', to: '' })
  const [goldRange, setGoldRange] = useState({ from: '', to: '' })
  const [diaRange, setDiaRange] = useState({ from: '', to: '' })
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<EntryForm>(emptyForm())
  const [editingId, setEditingId] = useState<string | null>(null)

  const existingClients = useMemo(() => Array.from(new Set(entries.map(e => e.clientName).filter(Boolean))).sort(), [entries])
  const salesPeople = useMemo(() => Array.from(new Set(entries.map(e => e.salesPerson).filter(Boolean))).sort(), [entries])

  // ---- filter + sort ----
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = entries.filter(e => {
      if (q && !(`${e.clientName} ${e.invoiceNo}`.toLowerCase().includes(q))) return false
      if (clientFilter && e.clientName !== clientFilter) return false
      if (categoryFilter && e.category !== categoryFilter) return false
      if (salesFilter && e.salesPerson !== salesFilter) return false
      if (!inRange(e.date, dateRange.from, dateRange.to)) return false
      if ((goldRange.from || goldRange.to) && !inRange(e.goldDueDate, goldRange.from, goldRange.to)) return false
      if ((diaRange.from || diaRange.to) && !inRange(e.diaDueDate, diaRange.from, diaRange.to)) return false
      if (dueFilter !== 'all') {
        const dl = daysLeft(soonestDue(e))
        if (dl === null) return false
        if (dueFilter === 'overdue' && dl >= 0) return false
        if (dueFilter === 'due7' && !(dl >= 0 && dl <= 7)) return false
        if (dueFilter === 'upcoming' && dl < 0) return false
      }
      return true
    })
    list = [...list].sort((a, b) => {
      if (sortBy === 'name') return a.clientName.localeCompare(b.clientName)
      if (sortBy === 'invoice') return a.invoiceNo.localeCompare(b.invoiceNo)
      if (sortBy === 'date') return String(b.date).localeCompare(String(a.date))
      const da = soonestDue(a), db = soonestDue(b)
      if (!da && !db) return 0
      if (!da) return 1
      if (!db) return -1
      return da.localeCompare(db)
    })
    return list
  }, [entries, search, clientFilter, categoryFilter, salesFilter, dueFilter, sortBy, dateRange, goldRange, diaRange])

  const sum = useMemo(() => {
    // Outstanding = only UNPAID entries. Marking an entry "Paid" removes its
    // amount from the gold / diamond / total outstanding and the overdue count.
    const unpaid = entries.filter(e => !e.paid)
    const gold = unpaid.reduce((t, e) => t + (e.goldAmount || 0), 0)
    const dia = unpaid.reduce((t, e) => t + (e.diaAmount || 0), 0)
    // Total gold weight still owed (unpaid entries) — founder's key metric.
    const goldWt = unpaid.reduce((t, e) => t + (e.goldWeight || 0), 0)
    const received = entries.filter(e => e.paid).reduce((t, e) => t + debtorTotal(e), 0)
    const overdue = unpaid.filter(e => { const dl = daysLeft(soonestDue(e)); return dl !== null && dl < 0 }).length
    const clients = new Set(entries.map(e => e.clientName).filter(Boolean)).size
    return { gold, dia, goldWt, total: gold + dia, received, overdue, clients, count: entries.length }
  }, [entries])

  const rangeActive = !!(dateRange.from || dateRange.to || goldRange.from || goldRange.to || diaRange.from || diaRange.to)
  const anyFilter = !!(search || clientFilter || categoryFilter || salesFilter || dueFilter !== 'all' || rangeActive)
  const clearFilters = () => {
    setSearch(''); setClientFilter(''); setCategoryFilter(''); setSalesFilter(''); setDueFilter('all')
    setDateRange({ from: '', to: '' }); setGoldRange({ from: '', to: '' }); setDiaRange({ from: '', to: '' })
  }
  const fmtRange = (r: { from: string; to: string }) => `${r.from || '…'} → ${r.to || '…'}`

  // Latest prior entry for a client (to repeat its gold/diamond term).
  const latestForClient = (name: string) => {
    const list = entries.filter(e => e.clientName.toLowerCase() === name.trim().toLowerCase())
    if (!list.length) return undefined
    return [...list].sort((a, b) => String(b.date).localeCompare(String(a.date)) || b.id.localeCompare(a.id))[0]
  }

  const openAdd = () => { setForm(emptyForm()); setEditingId(null); setShowModal(true) }
  const openEdit = (e: DebtorEntry) => {
    setForm({
      date: e.date, clientName: e.clientName, category: e.category || '', invoiceNo: e.invoiceNo,
      goldWeight: e.goldWeight ? String(e.goldWeight) : '', goldAmount: e.goldAmount ? String(e.goldAmount) : '',
      goldDueDate: e.goldDueDate, goldDays: diffDays(e.date, e.goldDueDate),
      diaAmount: e.diaAmount ? String(e.diaAmount) : '', diaDueDate: e.diaDueDate, diaDays: diffDays(e.date, e.diaDueDate),
      salesPerson: e.salesPerson, remarks: e.remarks,
    })
    setEditingId(e.id); setShowModal(true)
  }

  // When the client changes while ADDING, repeat that client's last term:
  // date stays today, due dates = today + the same gap used last time.
  const onClientChange = (name: string) => {
    if (editingId) { setForm(f => ({ ...f, clientName: name })); return }
    const prev = latestForClient(name)
    const base = form.date || todayStr()
    let gDays = '', gDue = '', dDays = '', dDue = ''
    if (prev) {
      const gGap = prev.goldDueDate ? diffDays(prev.date, prev.goldDueDate) : ''
      const dGap = prev.diaDueDate ? diffDays(prev.date, prev.diaDueDate) : ''
      if (gGap !== '') { gDays = gGap; gDue = plusDays(base, Number(gGap)) }
      if (dGap !== '') { dDays = dGap; dDue = plusDays(base, Number(dGap)) }
    }
    setForm(f => ({ ...f, clientName: name, goldDays: gDays, goldDueDate: gDue, diaDays: dDays, diaDueDate: dDue }))
  }
  // Changing the entry Date keeps the active terms (recompute due = date + days).
  const onDateChange = (d: string) => setForm(f => ({
    ...f, date: d,
    goldDueDate: Number.isFinite(parseInt(f.goldDays, 10)) ? plusDays(d, parseInt(f.goldDays, 10)) : f.goldDueDate,
    diaDueDate: Number.isFinite(parseInt(f.diaDays, 10)) ? plusDays(d, parseInt(f.diaDays, 10)) : f.diaDueDate,
  }))

  const save = () => {
    if (!form.clientName.trim()) { toast.error('Client name is required'); return }
    const prev = editingId ? entries.find(e => e.id === editingId) : undefined
    const entry: DebtorEntry = {
      id: editingId || `db-${Date.now()}`,
      date: form.date || todayStr(),
      clientName: form.clientName.trim(),
      category: form.category,
      invoiceNo: form.invoiceNo.trim(),
      goldWeight: parseFloat(form.goldWeight) || 0,
      goldAmount: parseFloat(form.goldAmount) || 0,
      goldDueDate: form.goldDueDate,
      diaAmount: parseFloat(form.diaAmount) || 0,
      diaDueDate: form.diaDueDate,
      salesPerson: form.salesPerson.trim(),
      remarks: form.remarks.trim(),
      flag: prev?.flag ?? false,
      paid: prev?.paid ?? false,
      paidDate: prev?.paidDate ?? '',
    }
    if (editingId) { updateEntry(editingId, entry); toast.success('Entry updated') }
    else { addEntry(entry); toast.success(`Added ${entry.clientName}`) }
    setShowModal(false)
  }

  const toggleFlag = (e: DebtorEntry) => updateEntry(e.id, { ...e, flag: !e.flag })

  // Pay button: mark received → removes this amount from Total Outstanding (green row).
  const togglePaid = (e: DebtorEntry) => {
    if (!e.paid) {
      if (!window.confirm(`Mark ${e.clientName}${e.invoiceNo ? ' · ' + e.invoiceNo : ''} as PAID?\n${money(debtorTotal(e))} will be removed from Total Outstanding.`)) return
      setPaid(e.id, true); toast.success(`${money(debtorTotal(e))} marked paid — removed from outstanding`)
    } else {
      setPaid(e.id, false); toast(`${e.clientName} moved back to unpaid`)
    }
  }

  // Short description of the active filters (shown on the exported report).
  const filterLabel = () => {
    const bits: string[] = []
    if (clientFilter) bits.push(`Client: ${clientFilter}`)
    if (categoryFilter) bits.push(`Category: ${categoryFilter}`)
    if (salesFilter) bits.push(`Sales: ${salesFilter}`)
    if (dateRange.from || dateRange.to) bits.push(`Date ${fmtRange(dateRange)}`)
    if (goldRange.from || goldRange.to) bits.push(`Gold Due ${fmtRange(goldRange)}`)
    if (diaRange.from || diaRange.to) bits.push(`Dia Due ${fmtRange(diaRange)}`)
    if (dueFilter !== 'all') bits.push(dueFilter === 'overdue' ? 'Overdue only' : dueFilter === 'due7' ? 'Due ≤7 days' : 'Upcoming')
    if (search) bits.push(`Search: ${search}`)
    return bits.length ? bits.join('  ·  ') : 'All entries'
  }

  const exportExcel = () => {
    if (!rows.length) { toast.error('Nothing to export'); return }
    const data = rows.map((e, i) => ({
      Sr: i + 1, Date: e.date, 'Client Name': e.clientName, Category: e.category || '', 'Invoice No': e.invoiceNo,
      'Gold Wt': e.goldWeight || '', 'Gold Amount': e.goldAmount || '',
      'Gold Due Date': e.goldDueDate, 'Gold Due (days)': dueDays(e.goldDueDate)?.text || '',
      'Diamond Amount': e.diaAmount || '',
      'Diamond Due Date': e.diaDueDate, 'Dia Due (days)': dueDays(e.diaDueDate)?.text || '',
      Total: debtorTotal(e), Payment: e.paid ? 'PAID' + (e.paidDate ? ` (${e.paidDate})` : '') : 'Outstanding',
      'Sales Person': e.salesPerson, Flag: e.flag ? '★' : '', Remarks: e.remarks,
    }))
    const ws = XLSX.utils.json_to_sheet(data)
    const outTot = rows.filter(e => !e.paid).reduce((t, e) => t + debtorTotal(e), 0)
    const gWtTot = rows.filter(e => !e.paid).reduce((t, e) => t + (e.goldWeight || 0), 0)
    XLSX.utils.sheet_add_aoa(ws, [[`Debtor Report  |  ${todayStr()}  |  ${filterLabel()}  |  ${rows.length} entries  |  Gold Wt ${wt(gWtTot)}  |  Outstanding ${money(outTot)}`]], { origin: 'A1' })
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Debtor Report')
    XLSX.writeFile(wb, `Debtor_Report_${todayStr()}.xlsx`)
    toast.success('Excel downloaded')
  }

  const exportPdf = () => {
    if (!rows.length) { toast.error('Nothing to export'); return }
    const doc = new jsPDF({ orientation: 'landscape' })
    const W = doc.internal.pageSize.getWidth()
    doc.setFillColor(17, 17, 17); doc.rect(0, 0, W, 22, 'F')
    doc.setTextColor(232, 190, 140); doc.setFont('times', 'bolditalic'); doc.setFontSize(16); doc.text('JP Gems & Jewels', 14, 14)
    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Debtor Report — Payment Follow-up', 14, 31)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(90, 90, 90)
    // Outstanding = unpaid only (PAID rows are removed from the totals below).
    const unpaidRows = rows.filter(e => !e.paid)
    const gTot = unpaidRows.reduce((t, e) => t + (e.goldAmount || 0), 0)
    const dTot = unpaidRows.reduce((t, e) => t + (e.diaAmount || 0), 0)
    const gWtTot = unpaidRows.reduce((t, e) => t + (e.goldWeight || 0), 0)
    doc.text(`${filterLabel()}   |   ${rows.length} entries   |   Gold Wt: ${wt(gWtTot)}   |   Outstanding: ${money(gTot + dTot)}   |   Date: ${todayStr()}`, 14, 37)
    // Due date cell = date + the days-from-due variance on a second line.
    const dueCell = (due: string) => { const v = dueDays(due); return due ? (v ? `${due}\n${v.text}` : due) : '' }
    autoTable(doc, {
      startY: 41,
      head: [['Sr', 'Date', 'Client Name', 'Cat', 'Invoice', 'Gold Wt', 'Gold Amt', 'Gold Due', 'Dia Amt', 'Dia Due', 'Total', 'Payment', 'Sales', 'Flag']],
      body: rows.map((e, i) => [String(i + 1), e.date, e.clientName, e.category || '', e.invoiceNo, e.goldWeight ? inr(e.goldWeight) : '',
        e.goldAmount ? inr(e.goldAmount) : '', dueCell(e.goldDueDate), e.diaAmount ? inr(e.diaAmount) : '', dueCell(e.diaDueDate),
        inr(debtorTotal(e)), e.paid ? 'PAID' : 'Outstanding', e.salesPerson, e.flag ? 'YES' : '']),
      foot: [['', '', '', '', 'OUTSTANDING', inr(gWtTot), inr(gTot), '', inr(dTot), '', inr(gTot + dTot), '', '', '']],
      showFoot: 'lastPage',
      styles: { fontSize: 7, cellPadding: 1.2, overflow: 'linebreak' },
      headStyles: { fillColor: [31, 56, 100], halign: 'center' },
      footStyles: { fillColor: [17, 17, 17], textColor: [232, 190, 140], fontStyle: 'bold', halign: 'right' },
      columnStyles: { 0: { halign: 'center' }, 3: { halign: 'center' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'center' }, 8: { halign: 'right' }, 9: { halign: 'center' }, 10: { halign: 'right' }, 11: { halign: 'center' }, 13: { halign: 'center' } },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      didParseCell: (d: any) => {
        if (d.section === 'body' && rows[d.row.index]?.flag) d.cell.styles.fillColor = [254, 226, 226]
        // paid rows → green tint + green Payment cell
        if (d.section === 'body' && rows[d.row.index]?.paid) {
          d.cell.styles.fillColor = [220, 252, 231]
          if (d.column.index === 11) { d.cell.styles.textColor = [21, 128, 61]; d.cell.styles.fontStyle = 'bold' }
        }
        // colour overdue due-date cells red (Gold Due = col 7, Dia Due = col 9)
        if (d.section === 'body' && (d.column.index === 7 || d.column.index === 9) && !rows[d.row.index]?.paid) {
          const dl = daysLeft(String(d.cell.raw || '').split('\n')[0])
          if (dl !== null && dl < 0) { d.cell.styles.textColor = [185, 28, 28]; d.cell.styles.fontStyle = 'bold' }
        }
      },
    })
    doc.save(`Debtor_Report_${todayStr()}.pdf`)
    toast.success('PDF downloaded')
  }

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><ClipboardList size={22} className="text-primary-600" /> Debtor Report</h1>
          <p className="page-subtitle">Who owes what — gold &amp; diamond amounts with due dates, for payment follow-up</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportPdf} title="Download the filtered report as PDF (send to sales person / client)" className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 font-medium"><FileText size={14} /> PDF</button>
          <button onClick={exportExcel} title="Download the filtered report as Excel" className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 font-medium"><FileSpreadsheet size={14} /> Excel</button>
          <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2"><Plus size={15} /> Add Party</button>
        </div>
      </div>

      {/* ===== Summary cards ===== */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Clients</p><p className="text-xl font-bold">{sum.clients}</p></div>
        <div className="card py-3 border-l-4 border-l-steel-400"><p className="text-xs text-steel-500">Total Gold Weight</p><p className="text-xl font-bold text-steel-700">{wt(sum.goldWt)}</p></div>
        <div className="card py-3 border-l-4 border-l-amber-500"><p className="text-xs text-steel-500">Gold Amount</p><p className="text-xl font-bold text-amber-600">{money(sum.gold)}</p></div>
        <div className="card py-3 border-l-4 border-l-indigo-500"><p className="text-xs text-steel-500">Diamond Amount</p><p className="text-xl font-bold text-indigo-600">{money(sum.dia)}</p></div>
        <div className="card py-3 border-l-4 border-l-primary-500"><p className="text-xs text-steel-500">Total Outstanding</p><p className="text-xl font-bold text-primary-600">{money(sum.total)}</p>{sum.received > 0 && <p className="text-[10px] text-green-600 font-medium">✓ {money(sum.received)} received</p>}</div>
        <div className={clsx('card py-3 border-l-4', sum.overdue > 0 ? 'border-l-red-500 bg-red-50/60' : 'border-l-green-500')}>
          <p className={clsx('text-xs font-medium', sum.overdue > 0 ? 'text-red-700' : 'text-steel-500')}>{sum.overdue > 0 ? '⚠ Overdue' : 'Overdue'}</p>
          <p className={clsx('text-xl font-extrabold', sum.overdue > 0 ? 'text-red-700' : 'text-steel-800')}>{sum.overdue}</p>
        </div>
      </div>

      {/* ===== Filters ===== */}
      <div className="card p-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-steel-200 bg-steel-50">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client or invoice no…"
              className="w-full pl-9 pr-8 py-1.5 text-sm border border-steel-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-400" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-steel-400 hover:text-red-600 text-base leading-none">×</button>}
          </div>
          <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} className="text-sm border border-steel-200 rounded-lg px-2 py-1.5 bg-white text-steel-700 max-w-[160px]">
            <option value="">All clients</option>
            {existingClients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="text-sm border border-steel-200 rounded-lg px-2 py-1.5 bg-white text-steel-700">
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={salesFilter} onChange={e => setSalesFilter(e.target.value)} className="text-sm border border-steel-200 rounded-lg px-2 py-1.5 bg-white text-steel-700 max-w-[150px]">
            <option value="">All sales persons</option>
            {salesPeople.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={dueFilter} onChange={e => setDueFilter(e.target.value as typeof dueFilter)} className="text-sm border border-steel-200 rounded-lg px-2 py-1.5 bg-white text-steel-700">
            <option value="all">All due dates</option>
            <option value="overdue">Overdue only</option>
            <option value="due7">Due within 7 days</option>
            <option value="upcoming">Upcoming (not overdue)</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)} className="text-sm border border-steel-200 rounded-lg px-2 py-1.5 bg-white text-steel-700">
            <option value="due">Sort: Due date (soonest)</option>
            <option value="name">Sort: Client A–Z</option>
            <option value="invoice">Sort: Invoice No</option>
            <option value="date">Sort: Newest entry</option>
          </select>
          {anyFilter && <button onClick={clearFilters} className="text-xs text-primary-600 hover:underline font-medium">Clear</button>}
          <span className="text-xs text-steel-500 ml-auto">{rows.length} of {entries.length}</span>
        </div>

        {/* ===== Table ===== */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-steel-50 border-b border-steel-200">
              <th className="table-header align-top">#</th>
              <th className="table-header align-top">Date
                <DateRangeFilter label="Date" from={dateRange.from} to={dateRange.to} onChange={(from, to) => setDateRange({ from, to })} /></th>
              <th className="table-header align-top">Client Name</th>
              <th className="table-header text-center align-top">Category</th>
              <th className="table-header align-top">Invoice No</th>
              <th className="table-header text-right align-top">Gold Wt</th>
              <th className="table-header text-right align-top">Gold Amt</th>
              <th className="table-header text-center align-top">Gold Due
                <DateRangeFilter label="Gold Due" from={goldRange.from} to={goldRange.to} onChange={(from, to) => setGoldRange({ from, to })} /></th>
              <th className="table-header text-right align-top">Dia Amt</th>
              <th className="table-header text-center align-top">Dia Due
                <DateRangeFilter label="Dia Due" from={diaRange.from} to={diaRange.to} onChange={(from, to) => setDiaRange({ from, to })} /></th>
              <th className="table-header text-right align-top">Total</th>
              <th className="table-header align-top">Sales Person</th>
              <th className="table-header text-center align-top">Payment</th>
              <th className="table-header text-center align-top">Flag</th>
              <th className="table-header text-center align-top">Action</th>
            </tr></thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={15} className="text-center text-steel-400 py-12">No entries yet. Click “Add Party” to add your first debtor.</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={15} className="text-center text-steel-400 py-12">No entries match the filters. <button onClick={clearFilters} className="text-primary-600 underline">Clear filters</button></td></tr>
              ) : rows.map((e, i) => {
                const overdue = (() => { const dl = daysLeft(soonestDue(e)); return dl !== null && dl < 0 })()
                return (
                  <tr key={e.id} className={clsx('table-row-hover', e.paid ? 'bg-green-50' : e.flag ? 'bg-red-50' : overdue && 'bg-red-50/40')}>
                    <td className="table-cell text-center text-steel-400">{i + 1}</td>
                    <td className="table-cell whitespace-nowrap text-steel-500">{e.date}</td>
                    <td className="table-cell font-medium text-steel-800 whitespace-nowrap">{e.clientName}</td>
                    <td className="table-cell text-center">
                      {e.category
                        ? <span className={clsx('px-1.5 py-0.5 rounded text-[11px] font-semibold', e.category === 'B-B' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}>{e.category}</span>
                        : <span className="text-steel-300">—</span>}
                    </td>
                    <td className="table-cell font-mono text-xs text-primary-700">{e.invoiceNo || '—'}</td>
                    <td className="table-cell text-right">{e.goldWeight ? e.goldWeight.toLocaleString('en-IN') : '—'}</td>
                    <td className="table-cell text-right">{e.goldAmount ? money(e.goldAmount) : '—'}</td>
                    <td className="table-cell text-center"><DueCell due={e.goldDueDate} /></td>
                    <td className="table-cell text-right">{e.diaAmount ? money(e.diaAmount) : '—'}</td>
                    <td className="table-cell text-center"><DueCell due={e.diaDueDate} /></td>
                    <td className="table-cell text-right font-bold text-primary-700 whitespace-nowrap">{money(debtorTotal(e))}</td>
                    <td className="table-cell whitespace-nowrap text-steel-600">{e.salesPerson || '—'}</td>
                    <td className="table-cell text-center">
                      <button onClick={() => togglePaid(e)}
                        title={e.paid ? `Paid${e.paidDate ? ' on ' + e.paidDate : ''} — click to undo` : 'Mark as paid (removes amount from Total Outstanding)'}
                        className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap',
                          e.paid ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-orange-100 text-orange-700 hover:bg-orange-200')}>
                        {e.paid ? <><CheckCircle2 size={11} /> Paid</> : <>Pay ▸</>}
                      </button>
                    </td>
                    <td className="table-cell text-center">
                      <button onClick={() => toggleFlag(e)} title="Flag this debtor to follow up" className={clsx('px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap', e.flag ? 'bg-red-600 text-white' : 'bg-steel-100 text-steel-500 hover:bg-red-100 hover:text-red-600')}>{e.flag ? '● Flagged' : 'Flag'}</button>
                    </td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(e)} className="p-1 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={13} /></button>
                        {user?.role === 'admin' && (
                          <button onClick={() => { if (window.confirm(`Delete ${e.clientName}${e.invoiceNo ? ' · ' + e.invoiceNo : ''}?`)) { removeEntry(e.id); toast.success('Deleted') } }} className="p-1 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete (founder only)"><Trash2 size={13} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="bg-steel-900 text-white font-semibold">
                <td className="px-3 py-2" colSpan={5}>OUTSTANDING ({rows.filter(e => !e.paid).length} unpaid of {rows.length} shown)</td>
                <td className="px-3 py-2 text-right text-amber-200">{wt(rows.filter(e => !e.paid).reduce((t, e) => t + (e.goldWeight || 0), 0))}</td>
                <td className="px-3 py-2 text-right">{money(rows.filter(e => !e.paid).reduce((t, e) => t + (e.goldAmount || 0), 0))}</td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-right">{money(rows.filter(e => !e.paid).reduce((t, e) => t + (e.diaAmount || 0), 0))}</td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-right text-amber-300">{money(rows.filter(e => !e.paid).reduce((t, e) => t + debtorTotal(e), 0))}</td>
                <td className="px-3 py-2" colSpan={4}></td>
              </tr></tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ===== Add / Edit modal ===== */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-steel-800">{editingId ? 'Edit Entry' : 'Add Party (Debtor)'}</h3>
              <button onClick={() => setShowModal(false)} className="text-steel-400 hover:text-steel-700 text-2xl leading-none">×</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-steel-600">Date</label>
                <input type="date" value={form.date} onChange={e => onDateChange(e.target.value)} className="form-input w-full mt-1 text-sm" /></div>
              <div><label className="text-xs font-medium text-steel-600">Client Name *</label>
                <input list="debtor-clients" value={form.clientName} onChange={e => onClientChange(e.target.value)} placeholder="Type or pick client…" className="form-input w-full mt-1 text-sm" />
                <datalist id="debtor-clients">{existingClients.map(c => <option key={c} value={c} />)}</datalist></div>
              <div><label className="text-xs font-medium text-steel-600">Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="form-input w-full mt-1 text-sm">
                  <option value="">— select —</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-steel-600">Invoice No</label>
                <input value={form.invoiceNo} onChange={e => setForm({ ...form, invoiceNo: e.target.value })} placeholder="e.g. JP-SO-0001" className="form-input w-full mt-1 text-sm" /></div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
              <p className="text-xs font-semibold text-amber-700 mb-2">💛 Gold</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className="text-xs font-medium text-steel-600">Gold Weight (g)</label>
                  <input type="number" step="0.001" value={form.goldWeight} onChange={e => setForm({ ...form, goldWeight: e.target.value })} placeholder="0" className="form-input w-full mt-1 text-sm" /></div>
                <div><label className="text-xs font-medium text-steel-600">Gold Amount ₹</label>
                  <input type="number" value={form.goldAmount} onChange={e => setForm({ ...form, goldAmount: e.target.value })} placeholder="0" className="form-input w-full mt-1 text-sm" /></div>
                <div><label className="text-xs font-medium text-steel-600">Gold Due <span className="text-steel-400 font-normal">(days / date)</span></label>
                  <div className="flex items-stretch gap-1.5 mt-1">
                    <input type="number" min="0" value={form.goldDays} placeholder="days" title="Type days (e.g. 30) to set the gold due date from the Date above"
                      onChange={e => { const v = e.target.value; const n = parseInt(v, 10); setForm(f => ({ ...f, goldDays: v, goldDueDate: Number.isFinite(n) ? plusDays(f.date || todayStr(), n) : f.goldDueDate })) }}
                      className="form-input shrink-0 w-14 text-sm text-center px-1" />
                    <input type="date" value={form.goldDueDate} onChange={e => { const dd = e.target.value; setForm(f => ({ ...f, goldDueDate: dd, goldDays: diffDays(f.date || todayStr(), dd) })) }} className="form-input flex-1 min-w-0 text-sm" />
                  </div></div>
              </div>
            </div>

            <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
              <p className="text-xs font-semibold text-indigo-700 mb-2">💎 Diamond</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-steel-600">Diamond Amount ₹</label>
                  <input type="number" value={form.diaAmount} onChange={e => setForm({ ...form, diaAmount: e.target.value })} placeholder="0" className="form-input w-full mt-1 text-sm" /></div>
                <div><label className="text-xs font-medium text-steel-600">Diamond Due <span className="text-steel-400 font-normal">(days / date)</span></label>
                  <div className="flex items-stretch gap-1.5 mt-1">
                    <input type="number" min="0" value={form.diaDays} placeholder="days" title="Type days (e.g. 60) to set the diamond due date from the Date above"
                      onChange={e => { const v = e.target.value; const n = parseInt(v, 10); setForm(f => ({ ...f, diaDays: v, diaDueDate: Number.isFinite(n) ? plusDays(f.date || todayStr(), n) : f.diaDueDate })) }}
                      className="form-input shrink-0 w-14 text-sm text-center px-1" />
                    <input type="date" value={form.diaDueDate} onChange={e => { const dd = e.target.value; setForm(f => ({ ...f, diaDueDate: dd, diaDays: diffDays(f.date || todayStr(), dd) })) }} className="form-input flex-1 min-w-0 text-sm" />
                  </div></div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-steel-600">Sales Person</label>
                <input value={form.salesPerson} onChange={e => setForm({ ...form, salesPerson: e.target.value })} placeholder="e.g. Amit" className="form-input w-full mt-1 text-sm" /></div>
              <div><label className="text-xs font-medium text-steel-600">Remarks</label>
                <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="note…" className="form-input w-full mt-1 text-sm" /></div>
            </div>

            <div className="flex items-center justify-between bg-steel-50 rounded-lg px-3 py-2">
              <span className="text-xs text-steel-500">Total (Gold + Diamond)</span>
              <span className="font-bold text-primary-700">{money((parseFloat(form.goldAmount) || 0) + (parseFloat(form.diaAmount) || 0))}</span>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowModal(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} className="btn-primary text-sm flex items-center gap-2">
                {editingId ? <><CheckCircle2 size={15} /> Save Changes</> : <><Plus size={15} /> Add Entry</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {sum.overdue > 0 && (
        <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={13} /> {sum.overdue} entr{sum.overdue === 1 ? 'y is' : 'ies are'} overdue — use “Overdue only” to follow up.</p>
      )}
    </div>
  )
}

export default DebtorReport
